import _ from 'lodash';
import {
  generateThresholdCoupon,
  processCoupon,
  getInitialDiscountResult
} from './utils';
import {
  couponFetchStatus,
  couponSourceTypeMap,
  couponTypes,
  PROMOTION_TYPE,
} from './constant';
import { isEmptyVal } from '@/utils';
import { compareDate, getNextPstCycleDate } from '@/utils/dateformat';
import { BILLING_CYCLE } from '@/common/constants';
import { getPlansMap } from '@/common/product';

/**
 * Discount calculation service class
 * Contains pure functions for all discount calculations
 *
 * @typedef {import('@/types/Base').CouponDiscountMapResultBean} CouponDiscountMapResultBean
 * @typedef {import('@/types/Base').PlanItemBean} PlanItemBean
 * @typedef {import('@/types/Base').SingleAmountPlansMapBean} SingleAmountPlansMapBean
 * @typedef {import('@/types/Base').PromotionDiscountInfoBean} PromotionDiscountInfoBean
 * @typedef {import('@/types/Base').PromotionInfoBean} PromotionInfoBean
 * @typedef {import('@/types/Base').BundleInfoBean} BundleInfoBean
 * @typedef {import('@/types/Base').DiscountPlanBean} DiscountPlanBean
 * @typedef {import('@/types/Base').ProcessCouponParamsBean} ProcessCouponParamsBean
 * @typedef {import('@/types/Base').CouponState} CouponState
 * @typedef {import('@/types/Base').SelectedPlansAmount} SelectedPlansAmount
 * @typedef {import('@/types/Base').NextBillAmount} NextBillAmount
 */
export class DiscountCalculationService {
  /**
   * Calculate discount for account-level coupon
   * @param {CouponState} couponState - Coupon state object
   * @returns {CouponDiscountMapResultBean} Discount result for account coupon
   */
  static calculateAccountCouponDiscount(couponState = {}) {
    const accountDiscount = couponState.account_discount || 0;
    return {
      ...getInitialDiscountResult(),
      totalDiscount: Math.abs(Number(accountDiscount)),
    };
  }

  /**
   * Calculate discount for timebase coupon
   * @param {CouponState} couponState - Coupon state object
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @returns {CouponDiscountMapResultBean} Discount result for timebase coupon
   */
  static calculateTimeBaseCouponDiscount(couponState = {}, selectedPlans = [], planWithAmountMap = {}) {
    // Transform timebase coupon data to discount plan list
    const discountPlanList = _.chain(couponState.timeBase || [])
      .reduce((accumulator, item) => {
        const { plan_code: planCode, price_mapping_plan_code, ...rest } = item;

        // Add main plan discount
        const mainPlanDiscount = { planCode, ...rest };
        accumulator.push(mainPlanDiscount);

        // Add price mapping plan discount if exists
        if (price_mapping_plan_code) {
          accumulator.push({ planCode: price_mapping_plan_code, ...rest });
        }

        return accumulator;
      }, [])
      .filter(Boolean)
      .value();

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Calculate discount for marketing coupon
   * @param {CouponState} couponState - Coupon state object
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @param {Object} allGetters - All store getters (for threshold coupon)
   * @returns {CouponDiscountMapResultBean} Discount result for marketing coupon
   */
  static calculateMarketingCouponDiscount(couponState = {}, selectedPlans = [], planWithAmountMap = {}, allGetters = {}) {
    const { couponSourceType } = couponState;

    // Handle threshold coupon separately
    if (couponSourceType === couponSourceTypeMap.threshold) {
      return generateThresholdCoupon(couponState, allGetters);
    }

    // Merge discount info and extension for plan increase cases (e.g., zoom one pro)
    const fullDiscountInfo = {
      ...couponState.discountInfo,
      ...couponState.discountInfoExtension
    };

    // Transform discount info to discount plan list
    const discountPlanList = _.chain(fullDiscountInfo)
      .keys()
      .map((planCode) => {
        const discountInfo = _.get(fullDiscountInfo, [planCode], {});
        const {
          expireDate,
          discount,
          couponDiscountType: discountType,
          couponPeriod,
          couponDefType = couponTypes.MARKETING,
        } = discountInfo;

        return {
          planCode,
          expireDate,
          discount,
          discountType,
          couponPeriod,
          couponDefType,
        };
      })
      .value();

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Calculate discount based on coupon type
   * @param {CouponState} couponState - Coupon state object
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @param {string} verifyCouponStatus - Coupon verification status
   * @param {Object} allGetters - All store getters (for complex calculations)
   * @returns {CouponDiscountMapResultBean} Discount result based on coupon type
   */
  static calculateCouponDiscount(couponState = {}, selectedPlans = [], planWithAmountMap = {}, verifyCouponStatus = couponFetchStatus.idle, allGetters = {}) {
    const { couponType } = couponState;

    // Route to appropriate discount calculation based on coupon type
    switch (couponType) {
      case couponTypes.ACCOUNT:
        return this.calculateAccountCouponDiscount(couponState);

      case couponTypes.TIMEBASE:
        return this.calculateTimeBaseCouponDiscount(couponState, selectedPlans, planWithAmountMap);

      case couponTypes.MARKETING:
        // Only calculate discount if coupon verification is successful or pending
        const isValidStatus = [couponFetchStatus.successfully, couponFetchStatus.pending]
          .includes(verifyCouponStatus);
        return isValidStatus
          ? this.calculateMarketingCouponDiscount(couponState, selectedPlans, planWithAmountMap, allGetters)
          : getInitialDiscountResult();

      default:
        return getInitialDiscountResult();
    }
  }

  /**
   * Calculate the next bill discount for each billing cycle
   * @param {Object} planWithDiscount - Discount info for each plan
   * @param {Object} selectedPlanMap - Selected plan details
   * @returns {Object} Discount summary for each billing cycle
   */
  static calculateNextBillDiscount(planWithDiscount = {}, selectedPlanMap = {}) {
    // Initialize discount result for each billing cycle
    const discountResult = getInitialDiscountResult();

    // Get next cycle dates with PDT offset -7
    const nextCycleDates = getNextPstCycleDate({ offset: -7 });

    // Map billing cycle to result key and next cycle date
    const cycleMap = {
      [BILLING_CYCLE.MONTHLY]: { key: 'monthlyDiscount', nextDate: nextCycleDates.nextMonthly },
      [BILLING_CYCLE.ANNUAL]: { key: 'annualDiscount', nextDate: nextCycleDates.nextAnnual },
      [BILLING_CYCLE.QUARTERLY]: { key: 'quarterlyDiscount', nextDate: nextCycleDates.nextQuarterly },
      [BILLING_CYCLE.TWO_YEARS]: { key: 'twoYearsDiscount', nextDate: nextCycleDates.nextTwoYears },
      [BILLING_CYCLE.THREE_YEARS]: { key: 'threeYearsDiscount', nextDate: nextCycleDates.nextThreeYears },
      [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: { key: 'annualTermMonthlyDiscount', nextDate: nextCycleDates.nextAnnual },
    };

    // Iterate all plans with discount
    _.forEach(planWithDiscount, (discountInfo, planCode) => {
      const curPlan = _.get(selectedPlanMap, planCode, {});
      const isOneMonth = _.get(curPlan, 'isOneMonth', false);
      const billingCycle = _.get(curPlan, 'billing_cycle');
      if (isOneMonth || billingCycle === BILLING_CYCLE.ONE_TIME) return; // Skip one month/one time plans

      const { expireDate, forever = false, discountAmount = 0 } = discountInfo;
      const cycleConf = cycleMap[billingCycle];
      if (!cycleConf) return;

      // If forever, always add discount; else, check expire date
      if (forever || compareDate(expireDate, cycleConf.nextDate)) {
        discountResult[cycleConf.key] += discountAmount;
      }
    });

    return discountResult;
  }

  /**
   * Calculate the next bill amount for each billing cycle after applying all discounts
   * @param {SelectedPlansAmount} selectedPlansAmount - Selected plans amount object
   * @param {Object} promotionDiscountInfoMap - Promotion discount info map
   * @param {Object} discountWithAmountMap - Coupon discount info map
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @returns {NextBillAmount} Object containing discount and charge details for each billing cycle
   */
  static calculateNextBillAmount(selectedPlansAmount = {}, promotionDiscountInfoMap = {}, discountWithAmountMap = {}, selectedPlans = [], couponState = {}) {
    const {
      annualAmount = 0,
      monthlyAmount = 0,
      quarterlyAmount = 0,
      twoYearsAmount = 0,
      threeYearsAmount = 0,
      annualTermMonthlyAmount = 0,
    } = selectedPlansAmount;

    const selectedPlanMap = getPlansMap(selectedPlans);
    const couponSourceType = couponState.couponSourceType;

    // Get all discount lists from promotions and coupons
    const allDiscountLists = [
      ...this.getNextBillDiscountListForPromotion(promotionDiscountInfoMap, selectedPlanMap),
      ...this.getNextBillDiscountListForCoupon(discountWithAmountMap, selectedPlanMap, couponSourceType),
    ];

    // Aggregate all discounts from promotions and coupons
    const discountMap = _.reduce(allDiscountLists, (result, item) => {
      // Sum up discounts for each billing cycle
      result.monthlyDiscount += item.monthlyDiscount || 0;
      result.annualDiscount += item.annualDiscount || 0;
      result.quarterlyDiscount += item.quarterlyDiscount || 0;
      result.twoYearsDiscount += item.twoYearsDiscount || 0;
      result.threeYearsDiscount += item.threeYearsDiscount || 0;
      result.annualTermMonthlyDiscount += item.annualTermMonthlyDiscount || 0;
      return result;
    }, getInitialDiscountResult());

    // Calculate final charges after applying discounts
    const charges = {
      monthlyCharge: monthlyAmount - discountMap.monthlyDiscount,
      annualCharge: annualAmount - discountMap.annualDiscount,
      quarterlyCharge: quarterlyAmount - discountMap.quarterlyDiscount,
      twoYearsCharge: twoYearsAmount - discountMap.twoYearsDiscount,
      threeYearsCharge: threeYearsAmount - discountMap.threeYearsDiscount,
      annualTermMonthlyCharge: annualTermMonthlyAmount - discountMap.annualTermMonthlyDiscount,
    };

    return {
      ...discountMap,
      ...charges,
    };
  }

  /**
   * Get the discount list for the next bill from all promotion discounts
   * @param {Object} promotionDiscountInfoMap - Promotion discount info map
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @returns {Array} List of discount summaries for each billing cycle from promotions
   */
  static getNextBillDiscountListForPromotion(promotionDiscountInfoMap = {}, selectedPlanMap = {}) {
    // Iterate over each promotion discount item and compute the next bill discount if available
    return _.chain(promotionDiscountInfoMap)
      .values()
      .map((item) => {
        const planWithDiscount = item.discountPlans;
        // Skip if discountPlans is empty
        if (isEmptyVal(planWithDiscount)) return undefined;
        return this.calculateNextBillDiscount(planWithDiscount, selectedPlanMap);
      })
      .filter(Boolean) // Remove undefined results
      .value();
  }

  /**
   * Get the discount list for the next bill from coupon discounts
   * @param {Object} discountWithAmountMap - Coupon discount info map
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {string} couponSourceType - Coupon source type
   * @returns {Array} List containing discount summary for each billing cycle from coupon
   */
  static getNextBillDiscountListForCoupon(discountWithAmountMap = {}, selectedPlanMap = {}, couponSourceType = '') {
    const planWithDiscount = discountWithAmountMap.discountPlans;

    // Only process if coupon is not threshold type and discountPlans is not empty
    if (couponSourceType !== couponSourceTypeMap.threshold && !isEmptyVal(planWithDiscount)) {
      return [this.calculateNextBillDiscount(planWithDiscount, selectedPlanMap)];
    }
    return [];
  }

  /**
   * Calculate discount info for soft bundle promotion
   * @param {Object} bundleInfo - Soft bundle information
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @returns {CouponDiscountMapResultBean} Discount result for soft bundle
   */
  static calculatePromotionDiscountInfoForSoftBundle(bundleInfo = {}, selectedPlans = [], planWithAmountMap = {}) {
    if (isEmptyVal(bundleInfo)) {
      return getInitialDiscountResult();
    }

    const { discountPlanList } = bundleInfo;

    return processCoupon({
      discountPlanList,
      selectedPlanList: selectedPlans,
      planWithAmountMap,
    });
  }

  /**
   * Compute discount info map for all promotion types
   * @param {Object} promotionInfo - Promotion information object
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @returns {Object} Discount info map for each promotion type
   */
  static computePromotionDiscountInfoMap(promotionInfo = {}, selectedPlans = [], planWithAmountMap = {}) {
    const result = {};

    // Process softBundle promotion type
    const softBundleInfo = promotionInfo[PROMOTION_TYPE.softBundle];
    if (softBundleInfo) {
      const isActive = [couponFetchStatus.pending, couponFetchStatus.successfully]
        .includes(softBundleInfo.status);

      result[PROMOTION_TYPE.softBundle] = isActive
        ? this.calculatePromotionDiscountInfoForSoftBundle(softBundleInfo, selectedPlans, planWithAmountMap)
        : getInitialDiscountResult();
    }

    return result;
  }
}
